import React, { memo, useState, useEffect, useRef, useMemo } from 'react';
import cn from 'classnames';
import { useTranslation } from 'next-i18next';
import { debounce } from 'lodash';

import { Spinner } from 'components';
import SelectionTickMark from 'components/form/SelectionTickMark';
import useScrollOffset from 'hooks/useScrollOffset';
import { useVisibleOnIntersect } from 'hooks/useVisibleOnIntersect';
import { useResponsive } from 'hooks/useResponsive';
import { useNotification } from 'hooks/useNotification';
import { useCandidate } from 'hooks/useCandidate';
import { useCandidateOnboarding } from 'hooks/useCandidateOnboarding';
import { OpportunityTypesResponse } from 'lib/models/opportunity';
import { MATCHING_PREFERENCE_STEPS } from 'lib/models/candidate';
import { Footer } from '../Footer/Footer.component';
import { SidebarMP } from '../SidebarMP/SidebarMP.component';
import MobileHeaderMp from '../MobileHeaderMp';

import styles from '../Onboarding.module.scss';

export interface OpportunityTypeProps {
  /** Custom class name */
  className?: string;
  candidateSelectedOpportunities: Array<string>;
  onNextClick: ({
    opportunitySelected,
    isBackClicked,
  }: {
    opportunitySelected: Array<string>;
    isBackClicked?: boolean;
  }) => void;
  isLoading: boolean;
  isCandidateOnboardingPage?: boolean;
  openOnModal?: boolean;
  isMatchingPreferences: boolean;
  opportunityTypes: OpportunityTypesResponse | undefined;
  onStepSelect?: (step: MATCHING_PREFERENCE_STEPS) => void;
}

export const OpportunityType: React.FC<OpportunityTypeProps> = memo(
  ({
    className = '', // custom class name
    candidateSelectedOpportunities,
    onNextClick,
    isCandidateOnboardingPage,
    isLoading,
    openOnModal = false,
    isMatchingPreferences,
    opportunityTypes,
    onStepSelect,
  }: OpportunityTypeProps) => {
    const screens = useResponsive();
    const notificationInstance = useNotification();
    const [t] = useTranslation('candidate-onboarding');
    const { isWindowScrolled } = useScrollOffset();
    const bottomRef = useRef<HTMLDivElement>(null);

    const { updateCandidateInfo } = useCandidate();

    const contentClassNames = cn(styles.container, className);

    const [selectedOpportunity, setSelectedOpportunity] = useState<
      Array<string>
    >([]);
    const [fullyScrolled, setFullyScrolled] = useState(false);

    useEffect(() => {
      if (candidateSelectedOpportunities.length) {
        setSelectedOpportunity(candidateSelectedOpportunities);
      }
    }, [candidateSelectedOpportunities]);
    const isReady = useMemo(
      () => !!selectedOpportunity.length,
      [selectedOpportunity]
    );

    const { isValueEdited, setValueEdited, onBackClickMPMobile } =
      useCandidateOnboarding({ isReady });

    useVisibleOnIntersect({
      elRef: bottomRef,
      threshold: 0,
      onIntersect: () => setFullyScrolled(true),
      onHidden: () => setFullyScrolled(false),
      rootMargin: '0px',
    });

    const onSubmit = (isBackClicked?: boolean) => {
      setValueEdited(false);
      onNextClick({ opportunitySelected: selectedOpportunity, isBackClicked });
    };

    const apiCallToUpdate = async (modifiedOpportunity: Array<string>) => {
      try {
        await updateCandidateInfo({
          partialCandidateAttributes: {
            opportunity_types: modifiedOpportunity,
          },
        });
      } catch (error) {
        notificationInstance.handleExceptionError(error);
      }
    };

    const debouncedAPICall = React.useRef(
      debounce(async (modifiedOpportunity) => {
        await apiCallToUpdate(modifiedOpportunity);
      }, 3000)
    ).current;

    const onOpportunityCheckboxClick = (opportunityType: string) => {
      setValueEdited(true);
      const index = selectedOpportunity?.findIndex(
        (type) => type === opportunityType
      );
      let modifiedOpportunity = [];
      if (index === -1)
        modifiedOpportunity = [...selectedOpportunity, opportunityType];
      else
        modifiedOpportunity =
          selectedOpportunity &&
          selectedOpportunity.filter((item) => item !== opportunityType);

      /** For MP
       * Desktop: Autosave on every change
       *          Do not make changes when nothing is selected on mandatory step
       * Mobile: Save on CTA click
       */
      setSelectedOpportunity(modifiedOpportunity);
      if (modifiedOpportunity.length === 0) return;

      if (isMatchingPreferences && !screens.sm) {
        debouncedAPICall(modifiedOpportunity);
      }
    };

    const onChevronClick = () => {
      if (isValueEdited) {
        onBackClickMPMobile();
      } else {
        onSubmit(true);
      }
    };

    if (!opportunityTypes) {
      return (
        <div className={styles.spinner}>
          <Spinner />
        </div>
      );
    }

    return (
      <div>
        <MobileHeaderMp
          title={t('matching-preferences.opportunity-types')}
          onChevronClick={onChevronClick}
        />
        <div className={contentClassNames} data-testid="OpportunityType">
          {isMatchingPreferences && onStepSelect ? (
            <SidebarMP
              isReady={isReady}
              onStepSelect={onStepSelect}
              className={styles.sidebar}
            />
          ) : null}
          <div
            className={cn(
              { [styles.content]: !openOnModal },
              { [styles.modalContent]: openOnModal },
              { [styles['content-mp']]: !openOnModal && isMatchingPreferences }
            )}
          >
            {openOnModal ? (
              <p className={styles.modalTitle}>{t('opportunity-type.title')}</p>
            ) : (
              <p className={styles.setup}>{t('opportunity-type.title')}</p>
            )}
            <p className={styles.desc}>{t('opportunity-type.subtitle')}</p>
            <div
              className={cn(styles.checkboxes, {
                [styles.modalCheckboxes]: openOnModal,
              })}
            >
              {opportunityTypes?.map((opportunity) => (
                <SelectionTickMark
                  key={opportunity.id}
                  checked={selectedOpportunity?.some(
                    (id) => opportunity.id === id
                  )}
                  isTickSquare={true}
                  onChange={() => onOpportunityCheckboxClick(opportunity.id)}
                  className={cn(styles.selectionbox, {
                    [styles.selectionboxOpportunityOnboarding]:
                      isCandidateOnboardingPage,
                  })}
                >
                  <span
                    className={cn(styles.displayname, {
                      [styles['display-name-dark']]: isCandidateOnboardingPage,
                    })}
                  >
                    {opportunity.display_name}
                  </span>
                  {isMatchingPreferences ? null : (
                    <p className={styles.labelDesc}>
                      {opportunity.description}
                    </p>
                  )}
                </SelectionTickMark>
              ))}
            </div>
            <div ref={bottomRef}></div>
          </div>

          <Footer
            isSecondaryCtaDisabled={!isMatchingPreferences}
            isBackLoading={false}
            isLoading={isLoading}
            isReady={isReady}
            onSubmit={onSubmit}
            isWindowScrolled={isWindowScrolled}
            isFullyScrolled={fullyScrolled}
            isMatchingPreference={isMatchingPreferences}
          />
        </div>
      </div>
    );
  }
);

OpportunityType.displayName = 'OpportunityType';
